import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useProgress } from "../Context/ProgressContextClean.js";
import { UPLIFT_EMOJIS } from "./CommunityActivity.js";
import { Theme } from "../Theme.js";
import { getCurrentUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
const COLORS = Theme.colors;
const SPACING = Theme.spacing;
const RADIUS = Theme.radius;
const client = generateClient();
const RADIUS_LG = 12;
const SHADOW = {
  xs: "0 1px 3px rgba(0,0,0,0.08)",
  sm: "0 2px 8px rgba(0,0,0,0.08)",
};

// Shared control width for dropdown-like buttons (reduced to fit 350px page)
const CONTROL_WIDTH = 140;
const MOMENTUM_WARNING_DAYS = 25;
const MOMENTUM_DECLINE_DAYS = 30;
const MOMENTUM_DECLINE_STEP_DAYS = 7;

const listPersonalProgressSeasons = /* GraphQL */ `
  query ListPersonalProgressSeasons($filter: ModelPersonalProgressFilterInput) {
    listPersonalProgresses(filter: $filter, limit: 100) {
      items {
        id
        userID
        activeSeason
        springLeaves
        springFlowers
        springFruit
        summerLeaves
        summerFlowers
        summerFruit
        autumnLeaves
        autumnFlowers
        autumnFruit
        winterLeaves
        winterFlowers
        winterFruit
        activeSeasonChangedAt
        activeSeasonLastEngagementAt
        updatedAt
      }
    }
  }
`;

const createPersonalProgressSeason = /* GraphQL */ `
  mutation CreatePersonalProgressSeason($input: CreatePersonalProgressInput!) {
    createPersonalProgress(input: $input) {
      id
      activeSeason
      springLeaves
      springFlowers
      springFruit
      summerLeaves
      summerFlowers
      summerFruit
      autumnLeaves
      autumnFlowers
      autumnFruit
      winterLeaves
      winterFlowers
      winterFruit
      activeSeasonChangedAt
      activeSeasonLastEngagementAt
    }
  }
`;

const updatePersonalProgressSeason = /* GraphQL */ `
  mutation UpdatePersonalProgressSeason($input: UpdatePersonalProgressInput!) {
    updatePersonalProgress(input: $input) {
      id
      activeSeason
      springLeaves
      springFlowers
      springFruit
      summerLeaves
      summerFlowers
      summerFruit
      autumnLeaves
      autumnFlowers
      autumnFruit
      winterLeaves
      winterFlowers
      winterFruit
      activeSeasonChangedAt
      activeSeasonLastEngagementAt
    }
  }
`;

const onCreatePersonalProgressSeason = /* GraphQL */ `
  subscription OnCreatePersonalProgressSeason {
    onCreatePersonalProgress {
      id
      userID
      year
      activeSeason
      springLeaves
      springFlowers
      springFruit
      summerLeaves
      summerFlowers
      summerFruit
      autumnLeaves
      autumnFlowers
      autumnFruit
      winterLeaves
      winterFlowers
      winterFruit
      activeSeasonChangedAt
      activeSeasonLastEngagementAt
      updatedAt
    }
  }
`;

const onUpdatePersonalProgressSeason = /* GraphQL */ `
  subscription OnUpdatePersonalProgressSeason {
    onUpdatePersonalProgress {
      id
      userID
      year
      activeSeason
      springLeaves
      springFlowers
      springFruit
      summerLeaves
      summerFlowers
      summerFruit
      autumnLeaves
      autumnFlowers
      autumnFruit
      winterLeaves
      winterFlowers
      winterFruit
      activeSeasonChangedAt
      activeSeasonLastEngagementAt
      updatedAt
    }
  }
`;

/* =========================================================
   CONSTANTS
========================================================= */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SEASONS = [
  {
    name: "Spring",
    months: [2, 3, 4],
    color: "#8C1456",
    mood: "Hopeful • Curious • Renewed",
    description:
      "A season of fresh starts, discovery, and renewed energy for prevention and growth.",
    solutions: ["Wholistic Health Assessment©"],
  },
  {
    name: "Summer",
    months: [5, 6, 7],
    color: "#2F5D50",
    mood: "Energized • Active • Motivated",
    description:
      "A season of momentum where practical tools help turn intention into sustainable action.",
    solutions: ["Wholistic Health App®"],
  },
  {
    name: "Autumn",
    months: [8, 9, 10],
    color: "#D4B65F",
    mood: "Connected • Grateful • Accomplished",
    description:
      "A season of community, shared wisdom, and celebration as growth becomes visible.",
    solutions: ["CoConnect Communities™"],
  },
  {
    name: "Winter",
    months: [11, 0, 1],
    color: "#0067B3",
    mood: "Reflective • Restoring • Resilient",
    description:
      "A season of rest, recovery, and intentional learning that prepares you for the next chapter.",
    solutions: ["Wholistic Health Program©", "Educational Resources"],
  },
];

const GROWTH_LEGEND_ITEMS = [
  { icon: "🍃", label: "Posts", meaning: "Each post adds leaf growth." },
  {
    icon: "🌸",
    label: "Uplifts Given",
    meaning: "Encouraging others adds flowers.",
  },
  {
    icon: "🍊",
    label: "Insights Received",
    meaning: "Receiving insight uplifts adds fruit.",
  },
];

const MILESTONE_LEGEND_ITEMS = [
  {
    icon: "🦋",
    label: "Breakthrough",
    meaning: "Your first consistency milestone. Momentum has started.",
  },
  {
    icon: "🐦",
    label: "Voice",
    meaning: "Your growth is becoming visible and expressive.",
  },
  {
    icon: "🪺",
    label: "Foundation",
    meaning: "You are building a steady home for healthier patterns.",
  },
  {
    icon: "🌼",
    label: "Bloom",
    meaning: "Your consistency is now maturing into stable progress.",
  },
  {
    icon: "✨",
    label: "Spark",
    meaning: "A glow appears with stronger consistency.",
  },
  {
    icon: "🍁",
    label: "Wisdom",
    meaning: "Experience and reflection are showing in your journey.",
  },
  {
    icon: "🌿",
    label: "Thrive",
    meaning: "Top consistency stage with fully active seasonal growth.",
  },
];

function milestonesForLevel(consistencyLevel) {
  return MILESTONE_LEGEND_ITEMS.slice(0, consistencyLevel).map(
    ({ icon, label }) => ({ icon, label }),
  );
}

// Map month index (0=Jan) to leaf color per design
function getLeafColor(monthIndex) {
  // March, April, May -> 8C1456
  if ([2, 3, 4].includes(monthIndex)) return "#8C1456";
  // June, July, August -> 2F5D50
  if ([5, 6, 7].includes(monthIndex)) return "#2F5D50";
  // September, October, November -> D4B65F
  if ([8, 9, 10].includes(monthIndex)) return "#D4B65F";
  // December, January, February -> 0067B3
  return "#0067B3";
}

function seasonFromMonth(monthIndex) {
  const found = SEASONS.find((s) => s.months.includes(monthIndex));
  return found ? found.name : "Spring";
}

function seededPoints(count, seedBase, xRange = 78, yRange = 55) {
  let seed = Math.abs(seedBase) + 1;
  const out = [];
  for (let i = 0; i < count; i += 1) {
    seed = (seed * 9301 + 49297) % 233280;
    const x = (seed / 233280) * xRange + 10;
    seed = (seed * 9301 + 49297) % 233280;
    const y = (seed / 233280) * yRange + 10;
    out.push({ x, y });
  }
  return out;
}

function normalizeId(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function idsEqual(a, b) {
  const na = normalizeId(a);
  const nb = normalizeId(b);
  return !!na && !!nb && na === nb;
}

function emptySeasonGrowth() {
  return SEASONS.reduce((growth, season) => {
    growth[season.name] = { leaves: 0, flowers: 0, fruit: 0 };
    return growth;
  }, {});
}

function seasonGrowthFromRecord(record) {
  const growth = emptySeasonGrowth();
  SEASONS.forEach((season) => {
    const prefix = season.name.toLowerCase();
    growth[season.name] = {
      leaves: Number(record?.[`${prefix}Leaves`] || 0),
      flowers: Number(record?.[`${prefix}Flowers`] || 0),
      fruit: Number(record?.[`${prefix}Fruit`] || 0),
    };
  });
  return growth;
}

function seasonGrowthInput(growth) {
  return SEASONS.reduce((input, season) => {
    const prefix = season.name.toLowerCase();
    const totals = growth[season.name] || {};
    input[`${prefix}Leaves`] = Number(totals.leaves || 0);
    input[`${prefix}Flowers`] = Number(totals.flowers || 0);
    input[`${prefix}Fruit`] = Number(totals.fruit || 0);
    return input;
  }, {});
}

const CARD_width_px = 300;
const CARD_WIDTH = CARD_width_px; // px

/* =========================================================
   TREE
========================================================= */

function Tree({
  growth = { leaves: 0, flowers: 0, fruit: 0 },
  seasonName,
  seasonColor,
  consistencyLevel = 0,
}) {
  const [open, setOpen] = useState(false);
  const totalSignals = growth.leaves + growth.flowers + growth.fruit;
  const canopyScale = Math.min(1.2, 0.78 + totalSignals / 180);
  const pulseStrength = Math.min(1, totalSignals / 80);

  const leafNodes = useMemo(
    () =>
      seededPoints(
        Math.min(growth.leaves, 16),
        growth.leaves * 17 + seasonName.length,
      ),
    [growth.leaves, seasonName],
  );

  const flowerNodes = useMemo(
    () =>
      seededPoints(
        Math.min(growth.flowers, 12),
        growth.flowers * 23 + seasonName.length * 3,
      ),
    [growth.flowers, seasonName],
  );

  const fruitNodes = useMemo(
    () =>
      seededPoints(
        Math.min(growth.fruit, 10),
        growth.fruit * 29 + seasonName.length * 5,
      ),
    [growth.fruit, seasonName],
  );

  const pulseNodes = useMemo(
    () => seededPoints(6, totalSignals * 13 + seasonName.length * 7, 82, 60),
    [totalSignals, seasonName],
  );

  const particleNodes = useMemo(
    () =>
      seededPoints(
        Math.min(14, 6 + consistencyLevel * 2),
        consistencyLevel * 43 + totalSignals,
        88,
        78,
      ),
    [consistencyLevel, totalSignals],
  );

  function renderCluster(points, emoji, size = 18, cls = "grow") {
    return points.map((pt, i) => (
      <span
        key={`${emoji}-${i}`}
        className={cls}
        style={{
          position: "absolute",
          left: `${pt.x}%`,
          top: `${pt.y}%`,
          fontSize: `${size}px`,
          animationDelay: `${i * 90}ms`,
        }}
      >
        {emoji}
      </span>
    ));
  }

  function renderLeaves(points, color) {
    return points.map((pt, i) => (
      <span
        key={`leaf-${i}`}
        className="leaf-sway"
        role="img"
        aria-label="leaf"
        style={{
          position: "absolute",
          left: `${pt.x}%`,
          top: `${pt.y}%`,
          width: 12,
          height: 12,
          background: color,
          borderRadius: "65% 35% 60% 40%",
          display: "inline-block",
          animationDelay: `${i * 120}ms`,
          boxShadow: `0 0 6px ${color}`,
        }}
      />
    ));
  }

  function renderMilestones() {
    return (
      <>
        {consistencyLevel >= 1 && <span style={styles.milestoneA}>🦋</span>}
        {consistencyLevel >= 2 && <span style={styles.milestoneB}>🐦</span>}
        {consistencyLevel >= 3 && <span style={styles.milestoneC}>🪺</span>}
        {consistencyLevel >= 4 && <span style={styles.milestoneD}>🌼</span>}
        {consistencyLevel >= 5 && (
          <span style={styles.milestoneE} className="firefly-glow">
            ✨
          </span>
        )}
        {consistencyLevel >= 6 && <span style={styles.milestoneF}>🍁</span>}
        {consistencyLevel >= 7 && <span style={styles.milestoneG}>🌿</span>}
      </>
    );
  }

  return (
    <div style={styles.treeWrapper}>
      <div style={styles.tree} onClick={() => setOpen(!open)} role="button">
        {/* Canopy */}
        <div
          style={{
            ...styles.canopy,
            transform: `scale(${canopyScale})`,
            background:
              consistencyLevel >= 7
                ? `radial-gradient(circle at 40% 35%, #ffffff, ${seasonColor})`
                : `radial-gradient(circle at 30% 25%, #ffffff, ${seasonColor})`,
            boxShadow: `0 0 ${16 + 26 * pulseStrength}px rgba(255,255,255,0.45), 0 0 ${28 + 24 * pulseStrength}px ${seasonColor}66`,
          }}
        >
          {renderLeaves(leafNodes, seasonColor || getLeafColor(2))}
          {renderCluster(flowerNodes, "🌸", 18, "grow")}
          {renderCluster(fruitNodes, "🍊", 18, "grow")}
          {pulseNodes.map((pt, i) => (
            <span
              key={`pulse-${i}`}
              className="neural-pulse"
              style={{
                left: `${pt.x}%`,
                top: `${pt.y}%`,
                animationDelay: `${i * 220}ms`,
              }}
            />
          ))}
          {particleNodes.map((pt, i) => (
            <span
              key={`particle-${i}`}
              className="glow-particle"
              style={{
                left: `${pt.x}%`,
                top: `${pt.y}%`,
                animationDelay: `${i * 180}ms`,
              }}
            />
          ))}
          {renderMilestones()}
        </div>

        {/* Trunk */}
        <div style={styles.trunk} />
      </div>

      {/* Overlay */}
      {open && (
        <div style={styles.overlay}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{seasonName}</div>
          <div>🍃 Posts: {growth.leaves}</div>
          <div>🌸 Uplifts Given: {growth.flowers}</div>
          <div>🍊 Insights Received: {growth.fruit}</div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   SEASON CARD
========================================================= */

function SeasonCard({ season, growth, isCurrent, onChoose, consistencyLevel }) {
  return (
    <section
      style={{
        ...styles.seasonCard,
        border: isCurrent
          ? `2px solid ${season.color}`
          : `1px solid ${COLORS.border}`,
      }}
    >
      <div style={styles.seasonCardHeader}>
        <h2 style={{ margin: 0 }}>{season.name}</h2>
        {isCurrent && (
          <span style={styles.currentSeasonBadge}>Current Season</span>
        )}
      </div>

      <div style={{ color: COLORS.muted, marginTop: 8 }}>{season.mood}</div>
      <p style={styles.seasonDescription}>{season.description}</p>

      <Tree
        seasonName={season.name}
        seasonColor={season.color}
        growth={growth}
        consistencyLevel={consistencyLevel}
      />

      <div style={styles.solutionTitle}>Recommended Solutions</div>
      <div style={styles.solutionWrap}>
        {season.solutions.map((s) => (
          <span key={s} style={styles.solutionChip}>
            {s}
          </span>
        ))}
      </div>

      <button
        onClick={() => onChoose(season.name)}
        style={{
          ...styles.chooseSeasonBtn,
          background: isCurrent ? season.color : COLORS.border,
          color: isCurrent ? "#fff" : "#333",
        }}
      >
        {isCurrent ? "In This Season" : "Choose This Season"}
      </button>
    </section>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function PersonalProgress({ setCurrentView }) {
  const {
    activities,
    uplifts,
    currentUserID,
    refresh,
    loading: progressLoading,
  } = useProgress();

  const STORAGE_ACTIVE_SEASON = "personal_progress_active_season";
  const STORAGE_ACTIVITY_SEASON_MAP = "personal_progress_activity_season_map";
  const STORAGE_UPLIFT_GIVEN_SEASON_MAP =
    "personal_progress_uplift_given_season_map";
  const STORAGE_UPLIFT_RECEIVED_SEASON_MAP =
    "personal_progress_uplift_received_season_map";
  const STORAGE_SEASON_MAP_INITIALIZED =
    "personal_progress_season_map_initialized_v1";
  const STORAGE_ACTIVE_SEASON_SET_AT = "personal_progress_active_season_set_at";
  const seasonStorageKey = useCallback(
    (baseKey) => {
      const normalizedUserId = normalizeId(currentUserID);
      return normalizedUserId ? `${baseKey}:${normalizedUserId}` : null;
    },
    [currentUserID],
  );
  const activeSeasonRecordRef = useRef([]);
  const progressRecordRef = useRef(null);
  const bootstrapRefreshAttempts = useRef(0);
  const [legendOpen, setLegendOpen] = useState(false);
  const [userAliases, setUserAliases] = useState([]);
  const [activeSeason, setActiveSeason] = useState(() => {
    return "Spring";
  });
  const [loadedSeasonStorageKey, setLoadedSeasonStorageKey] = useState(null);
  const [persistedSeasonGrowthByName, setPersistedSeasonGrowthByName] =
    useState(emptySeasonGrowth);
  const [seasonMaps, setSeasonMaps] = useState({
    activityMap: {},
    upliftGivenMap: {},
    upliftReceivedMap: {},
  });
  const [progressRecordId, setProgressRecordId] = useState(null);
  const [momentumClock, setMomentumClock] = useState(Date.now());
  const [mustChooseSeason, setMustChooseSeason] = useState(() => {
    try {
      return (
        localStorage.getItem("personalProgress.selectSeasonBeforeCommunity") ===
        "1"
      );
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const interval = setInterval(
      () => setMomentumClock(Date.now()),
      60 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, []);
  const chooseActiveSeason = useCallback(
    async (seasonName) => {
      if (!SEASONS.some((season) => season.name === seasonName)) return false;

      const activeSeasonKey = seasonStorageKey(STORAGE_ACTIVE_SEASON);
      const now = new Date().toISOString();
      try {
        if (activeSeasonKey) {
          localStorage.setItem(activeSeasonKey, seasonName);
        }
      } catch {}

      try {
        const record = progressRecordRef.current;
        const response = await client.graphql({
          query: record
            ? updatePersonalProgressSeason
            : createPersonalProgressSeason,
          variables: {
            input: record
              ? {
                  id: record.id,
                  activeSeason: seasonName,
                  activeSeasonChangedAt: now,
                  activeSeasonLastEngagementAt: now,
                }
              : {
                  userID: currentUserID,
                  year: new Date().getFullYear(),
                  journeyYearStartMonth: 3,
                  activeSeason: seasonName,
                  activeSeasonChangedAt: now,
                  activeSeasonLastEngagementAt: now,
                },
          },
        });
        const updatedRecord =
          response.data?.updatePersonalProgress ||
          response.data?.createPersonalProgress;
        if (updatedRecord) {
          progressRecordRef.current = updatedRecord;
          setProgressRecordId(updatedRecord.id);
          setPersistedSeasonGrowthByName(seasonGrowthFromRecord(updatedRecord));
        }
        setActiveSeason(seasonName);

        if (mustChooseSeason) {
          try {
            localStorage.removeItem(
              "personalProgress.selectSeasonBeforeCommunity",
            );
          } catch {}
          setMustChooseSeason(false);
          setCurrentView?.("CommunityActivity");
        }
        return true;
      } catch (error) {
        console.error("Failed to choose active season:", error);
        return false;
      }
    },
    [currentUserID, mustChooseSeason, seasonStorageKey, setCurrentView],
  );

  useEffect(() => {
    if (!currentUserID || progressRecordId) return;
    if (progressLoading) return;

    const hasData =
      (activities && activities.length > 0) || (uplifts && uplifts.length > 0);

    if (hasData) {
      return;
    }

    if (bootstrapRefreshAttempts.current >= 4) return;

    const nextAttempt = bootstrapRefreshAttempts.current + 1;
    const timer = setTimeout(() => {
      bootstrapRefreshAttempts.current = nextAttempt;
      refresh && refresh();
    }, 900 * nextAttempt);

    return () => clearTimeout(timer);
  }, [
    activities,
    uplifts,
    refresh,
    currentUserID,
    progressLoading,
    progressRecordId,
  ]);

  useEffect(() => {
    let cancelled = false;
    const key = seasonStorageKey(STORAGE_ACTIVE_SEASON);
    const defaultSeason = "Spring";
    activeSeasonRecordRef.current = [];
    progressRecordRef.current = null;
    setProgressRecordId(null);
    setPersistedSeasonGrowthByName(emptySeasonGrowth());
    if (!key) {
      setLoadedSeasonStorageKey(null);
      setActiveSeason(defaultSeason);
      return;
    }

    let localSeason = null;
    try {
      const saved = localStorage.getItem(key);
      localSeason = SEASONS.some((season) => season.name === saved)
        ? saved
        : null;
    } catch {}

    async function loadActiveSeason() {
      let record = null;
      try {
        const response = await client.graphql({
          query: listPersonalProgressSeasons,
          variables: { filter: { userID: { eq: currentUserID } } },
        });
        record = [...(response.data?.listPersonalProgresses?.items || [])]
          .filter((item) =>
            [currentUserID, ...userAliases].some((id) =>
              idsEqual(id, item?.userID),
            ),
          )
          .sort(
            (left, right) =>
              new Date(right.updatedAt || 0).getTime() -
              new Date(left.updatedAt || 0).getTime(),
          );
      } catch (error) {
        console.error("Failed to load active season preference:", error);
      }

      if (cancelled) return;
      activeSeasonRecordRef.current = record;
      progressRecordRef.current = record[0] || null;
      setProgressRecordId(record[0]?.id || null);
      setPersistedSeasonGrowthByName(seasonGrowthFromRecord(record[0]));
      const remoteSeason = record.find((item) =>
        SEASONS.some((season) => season.name === item?.activeSeason),
      )?.activeSeason;
      setActiveSeason(
        SEASONS.some((season) => season.name === remoteSeason)
          ? remoteSeason
          : localSeason || defaultSeason,
      );
      setLoadedSeasonStorageKey(key);
    }

    loadActiveSeason();
    return () => {
      cancelled = true;
    };
  }, [seasonStorageKey, currentUserID, userAliases]);

  useEffect(() => {
    if (!currentUserID) return;

    const applyRemoteSeason = (record) => {
      const belongsToCurrentUser = [currentUserID, ...userAliases].some((id) =>
        idsEqual(id, record?.userID),
      );
      if (
        !belongsToCurrentUser ||
        !SEASONS.some((season) => season.name === record?.activeSeason)
      ) {
        return;
      }

      activeSeasonRecordRef.current = [
        ...activeSeasonRecordRef.current.filter(
          (item) => item?.id !== record.id,
        ),
        record,
      ].sort(
        (left, right) =>
          new Date(right.updatedAt || 0).getTime() -
          new Date(left.updatedAt || 0).getTime(),
      );
      setActiveSeason(record.activeSeason);
      if (progressRecordRef.current?.id === record.id) {
        progressRecordRef.current = record;
        setPersistedSeasonGrowthByName(seasonGrowthFromRecord(record));
      }
    };

    const subscriptions = [
      client.graphql({ query: onCreatePersonalProgressSeason }).subscribe({
        next: ({ data }) => applyRemoteSeason(data?.onCreatePersonalProgress),
      }),
      client.graphql({ query: onUpdatePersonalProgressSeason }).subscribe({
        next: ({ data }) => applyRemoteSeason(data?.onUpdatePersonalProgress),
      }),
    ];

    return () =>
      subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [currentUserID, userAliases]);

  useEffect(() => {
    const activeSeasonKey = seasonStorageKey(STORAGE_ACTIVE_SEASON);
    if (!activeSeasonKey || loadedSeasonStorageKey !== activeSeasonKey) {
      return;
    }
    try {
      localStorage.setItem(activeSeasonKey, activeSeason);
    } catch {}

    async function persistActiveSeason() {
      try {
        const existingRecords = activeSeasonRecordRef.current;
        if (existingRecords.length > 0) {
          await Promise.all(
            existingRecords.map((record) =>
              client.graphql({
                query: updatePersonalProgressSeason,
                variables: { input: { id: record.id, activeSeason } },
              }),
            ),
          );
          return;
        }

        const response = await client.graphql({
          query: createPersonalProgressSeason,
          variables: {
            input: {
              userID: currentUserID,
              year: new Date().getFullYear(),
              journeyYearStartMonth: 3,
              activeSeason,
              activeSeasonChangedAt: new Date().toISOString(),
              activeSeasonLastEngagementAt: new Date().toISOString(),
            },
          },
        });
        activeSeasonRecordRef.current = [
          response.data?.createPersonalProgress,
        ].filter(Boolean);
        progressRecordRef.current = activeSeasonRecordRef.current[0] || null;
        setProgressRecordId(progressRecordRef.current?.id || null);
        setPersistedSeasonGrowthByName(
          seasonGrowthFromRecord(progressRecordRef.current),
        );
      } catch (error) {
        console.error("Failed to save active season preference:", error);
      }
    }

    persistActiveSeason();
  }, [activeSeason, currentUserID, seasonStorageKey, loadedSeasonStorageKey]);

  useEffect(() => {
    let cancelled = false;
    async function loadUserAliases() {
      try {
        const user = await getCurrentUser();
        if (cancelled) return;
        const aliases = [
          currentUserID,
          user?.userId,
          user?.username,
          user?.attributes?.sub,
          user?.signInDetails?.loginId,
        ].filter(Boolean);
        setUserAliases(Array.from(new Set(aliases)));
      } catch {
        if (!cancelled) setUserAliases(currentUserID ? [currentUserID] : []);
      }
    }
    loadUserAliases();
    return () => {
      cancelled = true;
    };
  }, [currentUserID]);

  const userIdSet = useMemo(() => {
    const out = new Set();
    [currentUserID, ...(userAliases || [])].forEach((v) => {
      const n = normalizeId(v);
      if (n) out.add(n);
    });
    return out;
  }, [currentUserID, userAliases]);

  const isCurrentUserId = useCallback(
    (value) => {
      const n = normalizeId(value);
      return !!n && userIdSet.has(n);
    },
    [userIdSet],
  );

  useEffect(() => {
    if (!currentUserID) return;
    const activeSeasonSetAtKey = seasonStorageKey(STORAGE_ACTIVE_SEASON_SET_AT);
    const activityMapKey = seasonStorageKey(STORAGE_ACTIVITY_SEASON_MAP);
    const upliftGivenMapKey = seasonStorageKey(STORAGE_UPLIFT_GIVEN_SEASON_MAP);
    const upliftReceivedMapKey = seasonStorageKey(
      STORAGE_UPLIFT_RECEIVED_SEASON_MAP,
    );
    const initializedKey = seasonStorageKey(STORAGE_SEASON_MAP_INITIALIZED);
    if (
      !activeSeasonSetAtKey ||
      !activityMapKey ||
      !upliftGivenMapKey ||
      !upliftReceivedMapKey ||
      !initializedKey
    ) {
      return;
    }
    try {
      const parseMap = (raw) => {
        if (!raw) return {};
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
          return {};
        }
      };

      const isRecent = (dateValue) => {
        if (!dateValue) return false;
        const t = new Date(dateValue).getTime();
        if (Number.isNaN(t)) return false;
        return Date.now() - t <= 24 * 60 * 60 * 1000;
      };
      const activeSeasonSetAt = Number(
        localStorage.getItem(activeSeasonSetAtKey) || 0,
      );
      const shouldUseActiveSeason = (dateValue) => {
        if (!activeSeasonSetAt) return isRecent(dateValue);
        if (!dateValue) return true;
        const t = new Date(dateValue).getTime();
        if (Number.isNaN(t)) return true;
        return t >= activeSeasonSetAt;
      };

      const activityMap = parseMap(localStorage.getItem(activityMapKey));
      const upliftGivenMap = parseMap(localStorage.getItem(upliftGivenMapKey));
      const upliftReceivedMap = parseMap(
        localStorage.getItem(upliftReceivedMapKey),
      );

      const initialized = localStorage.getItem(initializedKey) === "1";
      const useHistoricalMonthFallback = !initialized;

      let changed = false;
      const myPosts = (activities || []).filter(
        (p) =>
          isCurrentUserId(p.userID) &&
          !["share", "shareSeasonTree"].includes(p.actionName),
      );

      myPosts.forEach((p) => {
        if (!p?.id) return;
        const monthIdx =
          typeof p.monthIndex === "number"
            ? Math.max(0, Math.min(11, p.monthIndex - 1))
            : p.createdAt
              ? new Date(p.createdAt).getMonth()
              : new Date().getMonth();
        const shouldFollowActive = shouldUseActiveSeason(p.createdAt);
        const nextSeason = useHistoricalMonthFallback
          ? shouldFollowActive
            ? activeSeason
            : seasonFromMonth(monthIdx)
          : shouldFollowActive
            ? activeSeason
            : activityMap[p.id] || seasonFromMonth(monthIdx);
        if (!activityMap[p.id] || activityMap[p.id] !== nextSeason) {
          activityMap[p.id] = nextSeason;
          changed = true;
        }
      });

      (uplifts || []).forEach((u) => {
        const uid =
          u?.id ||
          `${u?.postID || "post"}-${u?.createdAt || "at"}-${u?.emoji || "emoji"}`;
        if (!uid) return;

        const d = u.createdAt ? new Date(u.createdAt) : null;
        const fallbackSeason = seasonFromMonth(
          d ? d.getMonth() : new Date().getMonth(),
        );

        const giver = u.giverUserID || u.userID || u.giver;
        if (isCurrentUserId(giver)) {
          const shouldFollowActive = shouldUseActiveSeason(u.createdAt);
          const nextSeason = useHistoricalMonthFallback
            ? shouldFollowActive
              ? activeSeason
              : fallbackSeason
            : shouldFollowActive
              ? activeSeason
              : upliftGivenMap[uid] || fallbackSeason;
          if (!upliftGivenMap[uid] || upliftGivenMap[uid] !== nextSeason) {
            upliftGivenMap[uid] = nextSeason;
            changed = true;
          }
        }

        const receiver =
          u.receiverUserID ||
          u.receiver ||
          (u.postID &&
            activities.find((a) => idsEqual(a.id, u.postID))?.userID);
        if (isCurrentUserId(receiver) && u.emoji === "💡") {
          const shouldFollowActive = shouldUseActiveSeason(u.createdAt);
          const nextSeason = useHistoricalMonthFallback
            ? shouldFollowActive
              ? activeSeason
              : fallbackSeason
            : shouldFollowActive
              ? activeSeason
              : upliftReceivedMap[uid] || fallbackSeason;
          if (
            !upliftReceivedMap[uid] ||
            upliftReceivedMap[uid] !== nextSeason
          ) {
            upliftReceivedMap[uid] = nextSeason;
            changed = true;
          }
        }
      });

      if (!initialized) {
        localStorage.setItem(initializedKey, "1");
      }

      if (changed || !initialized) {
        localStorage.setItem(activityMapKey, JSON.stringify(activityMap));
        localStorage.setItem(upliftGivenMapKey, JSON.stringify(upliftGivenMap));
        localStorage.setItem(
          upliftReceivedMapKey,
          JSON.stringify(upliftReceivedMap),
        );
      }

      // Keep UI in sync immediately so existing activity appears without requiring a new post.
      setSeasonMaps({
        activityMap,
        upliftGivenMap,
        upliftReceivedMap,
      });
    } catch (e) {
      console.error("Failed to maintain season assignment maps:", e);
    }
  }, [
    activities,
    uplifts,
    currentUserID,
    activeSeason,
    isCurrentUserId,
    seasonStorageKey,
    progressRecordId,
  ]);

  useEffect(() => {
    const record = progressRecordRef.current;
    if (!record || !currentUserID) return;

    const nextGrowth = emptySeasonGrowth();
    let latestActiveSeasonEngagementAt = null;
    const eventSeason = (item) => {
      if (SEASONS.some((season) => season.name === item?.season)) {
        return item.season;
      }
      const eventDate = item?.createdAt ? new Date(item.createdAt) : new Date();
      return seasonFromMonth(eventDate.getMonth());
    };

    (activities || []).forEach((activity) => {
      if (
        !isCurrentUserId(activity.userID) ||
        ["share", "shareSeasonTree"].includes(activity.actionName)
      ) {
        return;
      }
      nextGrowth[eventSeason(activity)].leaves += 1;
      if (
        eventSeason(activity) === activeSeason &&
        new Date(activity.createdAt || 0).getTime() >
          new Date(latestActiveSeasonEngagementAt || 0).getTime()
      ) {
        latestActiveSeasonEngagementAt = activity.createdAt;
      }
    });

    (uplifts || []).forEach((uplift) => {
      const season = eventSeason(uplift);
      const giver = uplift.giverUserID || uplift.userID || uplift.giver;
      if (
        isCurrentUserId(giver) &&
        (uplift.emoji === "👏" || uplift.emoji === "🤍")
      ) {
        nextGrowth[season].flowers += 1;
        if (
          season === activeSeason &&
          new Date(uplift.createdAt || 0).getTime() >
            new Date(latestActiveSeasonEngagementAt || 0).getTime()
        ) {
          latestActiveSeasonEngagementAt = uplift.createdAt;
        }
      }

      const receiver =
        uplift.receiverUserID ||
        uplift.receiver ||
        (uplift.postID &&
          activities.find((activity) => idsEqual(activity.id, uplift.postID))
            ?.userID);
      if (isCurrentUserId(receiver) && uplift.emoji === "💡") {
        const post = activities.find((activity) =>
          idsEqual(activity.id, uplift.postID),
        );
        const recipientSeason = eventSeason(post);
        nextGrowth[recipientSeason].fruit += 1;
        if (
          recipientSeason === activeSeason &&
          new Date(uplift.createdAt || 0).getTime() >
            new Date(latestActiveSeasonEngagementAt || 0).getTime()
        ) {
          latestActiveSeasonEngagementAt = uplift.createdAt;
        }
      }
    });

    const input = seasonGrowthInput(nextGrowth);
    if (
      latestActiveSeasonEngagementAt &&
      new Date(latestActiveSeasonEngagementAt).getTime() >
        new Date(record.activeSeasonLastEngagementAt || 0).getTime()
    ) {
      input.activeSeasonLastEngagementAt = latestActiveSeasonEngagementAt;
    }
    const currentGrowth = seasonGrowthFromRecord(record);
    if (
      JSON.stringify(nextGrowth) === JSON.stringify(currentGrowth) &&
      !input.activeSeasonLastEngagementAt
    ) {
      setPersistedSeasonGrowthByName(currentGrowth);
      return;
    }

    let cancelled = false;
    async function persistGrowth() {
      try {
        const response = await client.graphql({
          query: updatePersonalProgressSeason,
          variables: { input: { id: record.id, ...input } },
        });
        const updatedRecord = response.data?.updatePersonalProgress;
        if (cancelled || !updatedRecord) return;
        progressRecordRef.current = updatedRecord;
        setPersistedSeasonGrowthByName(seasonGrowthFromRecord(updatedRecord));
      } catch (error) {
        console.error("Failed to synchronize seasonal progress:", error);
      }
    }

    persistGrowth();
    return () => {
      cancelled = true;
    };
  }, [
    activities,
    uplifts,
    currentUserID,
    activeSeason,
    isCurrentUserId,
    progressRecordId,
  ]);

  function getConsistencyLevelForGrowth(growth) {
    const leaves = Number(growth?.leaves || 0);
    const flowers = Number(growth?.flowers || 0);
    const fruit = Number(growth?.fruit || 0);
    const score = leaves + flowers + fruit;

    let level = 0;
    if (score >= 2) level = 1;
    if (score >= 4) level = 2;
    if (score >= 7) level = 3;
    if (score >= 11) level = 4;
    if (score >= 16) level = 5;
    if (score >= 22) level = 6;
    if (score >= 30) level = 7;
    return level;
  }

  const seasonGrowthByName = useMemo(() => {
    if (progressRecordId) return persistedSeasonGrowthByName;
    const parseMap = (raw) => {
      if (!raw) return {};
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    };

    const out = SEASONS.reduce((acc, s) => {
      acc[s.name] = { leaves: 0, flowers: 0, fruit: 0 };
      return acc;
    }, {});

    if (!currentUserID) return out;

    try {
      const activityMap =
        seasonMaps.activityMap && typeof seasonMaps.activityMap === "object"
          ? seasonMaps.activityMap
          : parseMap(localStorage.getItem(STORAGE_ACTIVITY_SEASON_MAP));
      const upliftGivenMap =
        seasonMaps.upliftGivenMap &&
        typeof seasonMaps.upliftGivenMap === "object"
          ? seasonMaps.upliftGivenMap
          : parseMap(localStorage.getItem(STORAGE_UPLIFT_GIVEN_SEASON_MAP));
      const upliftReceivedMap =
        seasonMaps.upliftReceivedMap &&
        typeof seasonMaps.upliftReceivedMap === "object"
          ? seasonMaps.upliftReceivedMap
          : parseMap(localStorage.getItem(STORAGE_UPLIFT_RECEIVED_SEASON_MAP));

      (activities || []).forEach((p) => {
        if (
          !isCurrentUserId(p.userID) ||
          ["share", "shareSeasonTree"].includes(p.actionName) ||
          !p.id
        )
          return;
        const s = activityMap[p.id];
        if (out[s]) out[s].leaves += 1;
      });

      (uplifts || []).forEach((u) => {
        const uid =
          u?.id ||
          `${u?.postID || "post"}-${u?.createdAt || "at"}-${u?.emoji || "emoji"}`;
        const giver = u.giverUserID || u.userID || u.giver;
        if (isCurrentUserId(giver)) {
          const sg = upliftGivenMap[uid];
          if (out[sg]) out[sg].flowers += 1;
        }

        const receiver =
          u.receiverUserID ||
          u.receiver ||
          (u.postID &&
            activities.find((a) => idsEqual(a.id, u.postID))?.userID);
        if (isCurrentUserId(receiver) && u.emoji === "💡") {
          const sr = upliftReceivedMap[uid];
          if (out[sr]) out[sr].fruit += 1;
        }
      });
    } catch (e) {
      console.error("Failed to compute season growth map:", e);
    }

    return out;
  }, [
    activities,
    uplifts,
    currentUserID,
    seasonMaps.activityMap,
    seasonMaps.upliftGivenMap,
    seasonMaps.upliftReceivedMap,
    isCurrentUserId,
    progressRecordId,
    persistedSeasonGrowthByName,
  ]);

  const consistencyBySeason = useMemo(() => {
    const out = {};
    SEASONS.forEach((s) => {
      out[s.name] = getConsistencyLevelForGrowth(seasonGrowthByName[s.name]);
    });
    return out;
  }, [seasonGrowthByName]);

  const activeSeasonInactivityDays = useMemo(() => {
    const lastEngagement =
      progressRecordRef.current?.activeSeasonLastEngagementAt ||
      progressRecordRef.current?.activeSeasonChangedAt;
    const lastEngagementMs = new Date(lastEngagement || Date.now()).getTime();
    return Math.max(0, (Date.now() - lastEngagementMs) / 86400000);
  }, []);

  const dynamicConsistencyBySeason = useMemo(() => {
    const out = { ...consistencyBySeason };
    if (activeSeasonInactivityDays < MOMENTUM_DECLINE_DAYS) return out;

    const levelsToReduce =
      Math.floor(
        (activeSeasonInactivityDays - MOMENTUM_DECLINE_DAYS) /
          MOMENTUM_DECLINE_STEP_DAYS,
      ) + 1;
    out[activeSeason] = Math.max(
      0,
      (consistencyBySeason[activeSeason] || 0) - levelsToReduce,
    );
    return out;
  }, [activeSeason, activeSeasonInactivityDays, consistencyBySeason]);

  const shouldWarnMomentum =
    !mustChooseSeason &&
    activeSeasonInactivityDays >= MOMENTUM_WARNING_DAYS &&
    activeSeasonInactivityDays < MOMENTUM_DECLINE_DAYS;

  return (
    <>
      <div style={{ ...styles.page, paddingBottom: 24 }}>
        {mustChooseSeason && (
          <div style={styles.seasonPrompt} role="status">
            <strong>What Season Are You In?</strong>
            <span>
              Select a season below to continue to Community Activity.
            </span>
          </div>
        )}
        {shouldWarnMomentum && (
          <div style={styles.momentumWarning} role="alert">
            <strong>Your {activeSeason} momentum needs attention.</strong>
            <span>
              Engage in a community before 30 days of inactivity to keep its
              dynamic effects active.
            </span>
            <button
              type="button"
              onClick={() => setCurrentView?.("CommunityActivity")}
              style={styles.momentumWarningButton}
            >
              Go To Community Activity
            </button>
          </div>
        )}
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-PP-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "20px auto 30px auto",
          }}
        />
        <h2 style={{ marginTop: 0 }}>This is Your Season to Grow</h2>
        <p style={{ color: COLORS.muted }}>
          Start sharing and encouraging others to see your trees grow.
        </p>

        <div style={styles.seasonSelectorWrap}>
          {SEASONS.map((s) => (
            <button
              key={`selector-${s.name}`}
              onClick={() => chooseActiveSeason(s.name)}
              style={{
                ...styles.seasonSelectorBtn,
                background: activeSeason === s.name ? s.color : "#fff",
                color: activeSeason === s.name ? "#fff" : "#333",
                borderColor: s.color,
              }}
            >
              {s.name}
            </button>
          ))}
        </div>

        <div style={styles.seasonHint}>
          You stay in your selected season until you choose another.
        </div>

        <div style={styles.legendCard}>
          <button
            type="button"
            onClick={() => setLegendOpen((v) => !v)}
            style={styles.legendToggleBtn}
          >
            <span style={styles.legendTitle}>Season Legend</span>
            <span style={styles.legendToggleChevron} aria-hidden="true">
              {legendOpen ? "▴" : "▾"}
            </span>
          </button>

          {legendOpen && (
            <>
              <div style={styles.legendSectionTitle}>Growth Signals</div>
              <div style={styles.legendGrid}>
                {GROWTH_LEGEND_ITEMS.map((item) => (
                  <div key={`growth-${item.icon}`} style={styles.legendItem}>
                    <span style={styles.legendIcon} aria-hidden="true">
                      {item.icon}
                    </span>
                    <div>
                      <div style={styles.legendItemLabel}>{item.label}</div>
                      <div style={styles.legendItemMeaning}>{item.meaning}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ ...styles.legendSectionTitle, marginTop: 10 }}>
                Milestones And Dynamic Effects
              </div>
              <div style={styles.legendGrid}>
                {MILESTONE_LEGEND_ITEMS.map((item) => (
                  <div key={`milestone-${item.icon}`} style={styles.legendItem}>
                    <span style={styles.legendIcon} aria-hidden="true">
                      {item.icon}
                    </span>
                    <div>
                      <div style={styles.legendItemLabel}>{item.label}</div>
                      <div style={styles.legendItemMeaning}>{item.meaning}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.legendFootnote}>
                As your activity and consistency grow, additional particles and
                pulses can appear to reflect momentum over time.
              </div>
            </>
          )}
        </div>

        {SEASONS.map((s) => (
          <SeasonCard
            key={s.name}
            season={s}
            growth={
              seasonGrowthByName[s.name] || { leaves: 0, flowers: 0, fruit: 0 }
            }
            isCurrent={activeSeason === s.name}
            onChoose={chooseActiveSeason}
            consistencyLevel={dynamicConsistencyBySeason[s.name] || 0}
          />
        ))}

        {/* Motion */}
        <style>{`
        .grow {
          opacity: 0;
          transform: scale(0.6);
          animation: growIn 400ms ease forwards;
        }
        .leaf-sway {
          transform-origin: center;
          animation: leafSway 2600ms ease-in-out infinite alternate, growIn 420ms ease forwards;
        }
        .neural-pulse {
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: rgba(255,255,255,0.95);
          box-shadow: 0 0 16px rgba(255,255,255,0.85);
          animation: neuralPulse 2200ms ease-in-out infinite;
        }
        .glow-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          border-radius: 999px;
          background: rgba(255,255,255,0.85);
          box-shadow: 0 0 8px rgba(255,255,255,0.7);
          animation: particleFloat 3200ms ease-in-out infinite;
        }
        .firefly-glow {
          animation: firefly 2400ms ease-in-out infinite;
        }
        @keyframes growIn {
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes leafSway {
          0% { transform: rotate(-4deg) translateY(0px); }
          100% { transform: rotate(5deg) translateY(-1px); }
        }
        @keyframes neuralPulse {
          0%, 100% { transform: scale(0.7); opacity: 0.35; }
          50% { transform: scale(1.3); opacity: 1; }
        }
        @keyframes particleFloat {
          0% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-8px); opacity: 1; }
          100% { transform: translateY(-2px); opacity: 0.4; }
        }
        @keyframes firefly {
          0%, 100% { opacity: 0.25; filter: drop-shadow(0 0 1px #fff7b3); }
          50% { opacity: 1; filter: drop-shadow(0 0 6px #fff7b3); }
        }
      `}</style>
      </div>

      {/* Transformation section in its own container (match Growth Forest width/style, keep spacing) */}
      <div
        style={{
          ...styles.page,
          paddingTop: SPACING.lg,
          marginTop: SPACING.lg,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Transformation — Your Posts</h2>
        <TransformationView
          isCurrentUserId={isCurrentUserId}
          seasonGrowthByName={seasonGrowthByName}
          consistencyBySeason={dynamicConsistencyBySeason}
          activeSeason={activeSeason}
        />
      </div>
    </>
  );
}

function TransformationView({
  isCurrentUserId = () => false,
  seasonGrowthByName = {},
  consistencyBySeason = {},
  activeSeason,
}) {
  const {
    activities = [],
    joinedCommunities = [],
    uplifts = [],
    createActivity,
  } = useProgress();

  const STORAGE_COMM = "trans_selectedCommunity";
  const STORAGE_MONTH = "trans_selectedMonth";

  const [selectedCommunity, setSelectedCommunity] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_COMM) || "ALL";
    } catch {
      return "ALL";
    }
  });

  // selectedMonths: array of month indexes (0-11). Empty array means no filter (all months).
  const [selectedMonths, setSelectedMonths] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_MONTH);
      if (!s) return [];
      let parsed;
      try {
        parsed = JSON.parse(s);
      } catch (e) {
        parsed = s;
      }

      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "number") return [parsed];
      if (typeof parsed === "string") {
        // Accept comma-separated or single values from older storage formats
        const parts = parsed
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean);
        const nums = parts
          .map((p) => {
            const n = Number(p);
            return Number.isNaN(n) ? null : n;
          })
          .filter((n) => n !== null);
        return nums;
      }

      return [];
    } catch {
      return [];
    }
  });

  const [monthsOpen, setMonthsOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [selectedTreeSeason, setSelectedTreeSeason] = useState(activeSeason);
  const [selectedTreeCommunityIDs, setSelectedTreeCommunityIDs] = useState([]);

  useEffect(() => {
    setSelectedTreeSeason(activeSeason);
  }, [activeSeason]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COMM, selectedCommunity);
    } catch {}
  }, [selectedCommunity]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_MONTH, JSON.stringify(selectedMonths));
    } catch {}
  }, [selectedMonths]);

  const myPosts = (activities || [])
    .filter(
      (p) =>
        isCurrentUserId(p.userID) &&
        !["share", "shareSeasonTree"].includes(p.actionName),
    )
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const visible = myPosts.filter((p) => {
    // community filter
    if (selectedCommunity !== "ALL") {
      if (!(p.communities || []).includes(selectedCommunity)) return false;
    }

    // month filter (selectedMonths are 0-11). If no months selected, include all.
    if (Array.isArray(selectedMonths) && selectedMonths.length > 0) {
      const monthFromPost =
        typeof p.monthIndex === "number"
          ? Math.max(0, Math.min(11, p.monthIndex - 1))
          : p.createdAt
            ? new Date(p.createdAt).getMonth()
            : null;

      if (monthFromPost === null) return false;
      return selectedMonths.includes(monthFromPost);
    }

    return true;
  });

  function countUpliftsFor(postId, emoji) {
    return (uplifts || []).filter(
      (u) => idsEqual(u.postID, postId) && u.emoji === emoji,
    ).length;
  }

  function handleExport() {
    // simple print/export fallback
    window.print();
  }

  async function handleShare() {
    try {
      if (selectedCommunity === "ALL") {
        alert("Please choose a community before sharing.");
        return;
      }

      // Confirm with user before posting shared transformation
      const confirmMsg =
        "This could include private posts if you selected a month that includes private posts. Please confirm that you want your transformation journey posted to the community.";
      if (!window.confirm(confirmMsg)) return;

      // Create a community activity (post) in the selected community
      // include the list of post ids in threadID so the community feed can render them
      const postIDs = visible
        .slice(0, 5)
        .map((p) => p.id)
        .filter(Boolean);
      await createActivity({
        text: "My Transformation",
        mediaFiles: [],
        communities: [selectedCommunity],
        visibility: "PUBLIC",
        actionName: "share",
        threadID: JSON.stringify(postIDs),
      });

      alert("Posted to community.");
    } catch (e) {
      console.error("Share failed", e);
      alert("Unable to post to community. See console for details.");
    }
  }

  async function handleShareSeasonTree() {
    const season = SEASONS.find((item) => item.name === selectedTreeSeason);
    if (!season || selectedTreeCommunityIDs.length === 0) {
      alert("Choose a season and at least one community to share your tree.");
      return;
    }

    const growth = seasonGrowthByName[season.name] || {
      leaves: 0,
      flowers: 0,
      fruit: 0,
    };
    const consistencyLevel = Number(consistencyBySeason[season.name] || 0);
    const snapshot = {
      type: "season-tree",
      version: 1,
      seasonName: season.name,
      seasonColor: season.color,
      sharedAt: new Date().toISOString(),
      growth: {
        leaves: Number(growth.leaves || 0),
        flowers: Number(growth.flowers || 0),
        fruit: Number(growth.fruit || 0),
      },
      consistencyLevel,
      milestones: milestonesForLevel(consistencyLevel),
    };

    try {
      await createActivity({
        text: `My ${season.name} Season Tree`,
        mediaFiles: [],
        communities: selectedTreeCommunityIDs,
        visibility: "PUBLIC",
        actionName: "shareSeasonTree",
        season: season.name,
        seasonSnapshot: JSON.stringify(snapshot),
      });
      alert("Season tree shared with your selected communities.");
    } catch (error) {
      console.error("Unable to share season tree:", error);
      alert("Unable to share your season tree right now. Please try again.");
    }
  }

  return (
    <div>
      <section style={styles.seasonSharePanel}>
        <div style={styles.seasonShareTitle}>Share A Season Tree</div>
        <div style={styles.seasonShareControls}>
          <select
            value={selectedTreeSeason || ""}
            onChange={(event) => setSelectedTreeSeason(event.target.value)}
            style={styles.seasonShareSelect}
            aria-label="Season to share"
          >
            {SEASONS.map((season) => (
              <option key={season.name} value={season.name}>
                {season.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleShareSeasonTree}
            style={styles.seasonShareButton}
          >
            Share Tree
          </button>
        </div>
        <div style={styles.seasonShareCommunityList}>
          {joinedCommunities.map((community) => (
            <label key={community.id} style={styles.seasonShareCommunityItem}>
              <input
                type="checkbox"
                checked={selectedTreeCommunityIDs.includes(community.id)}
                onChange={() =>
                  setSelectedTreeCommunityIDs((selected) =>
                    selected.includes(community.id)
                      ? selected.filter((id) => id !== community.id)
                      : [...selected, community.id],
                  )
                }
              />
              <span>{community.name}</span>
            </label>
          ))}
          {joinedCommunities.length === 0 && (
            <span style={{ color: COLORS.muted, fontSize: 12 }}>
              Join a community to share your season tree.
            </span>
          )}
        </div>
      </section>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setCommunityOpen((s) => !s)}
              style={{
                padding: "6px 10px",
                width: CONTROL_WIDTH,
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: "white",
                cursor: "pointer",
              }}
            >
              {selectedCommunity === "ALL"
                ? "All Communities"
                : joinedCommunities.find((c) => c.id === selectedCommunity)
                    ?.name || "Community"}
            </button>

            {communityOpen && (
              <div
                style={{
                  position: "absolute",
                  top: 40,
                  left: 0,
                  width: 260,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "white",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  padding: 8,
                  boxShadow: SHADOW.sm,
                  zIndex: 40,
                }}
              >
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <button
                    onClick={() => {
                      setSelectedCommunity("ALL");
                      setCommunityOpen(false);
                    }}
                    style={{
                      padding: 6,
                      textAlign: "left",
                      cursor: "pointer",
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    All Communities
                  </button>

                  {joinedCommunities.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedCommunity(c.id);
                        setCommunityOpen(false);
                      }}
                      style={{
                        padding: 6,
                        textAlign: "left",
                        cursor: "pointer",
                        border: "none",
                        background: "transparent",
                      }}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ position: "relative" }}>
            <button
              onClick={() => setMonthsOpen((s) => !s)}
              style={{
                padding: "6px 10px",
                width: CONTROL_WIDTH,
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: "white",
                cursor: "pointer",
              }}
            >
              {Array.isArray(selectedMonths) && selectedMonths.length > 0
                ? `Months (${selectedMonths.length})`
                : "All Months"}
            </button>

            {monthsOpen && (
              <div
                style={{
                  position: "absolute",
                  top: 40,
                  left: 0,
                  width: 260,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "white",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 8,
                  padding: 8,
                  boxShadow: SHADOW.sm,
                  zIndex: 40,
                }}
              >
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button
                    onClick={() => setSelectedMonths(MONTHS.map((_, i) => i))}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: COLORS.border,
                      color: "#333",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setSelectedMonths([])}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: COLORS.border,
                      color: "#333",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>

                <div>
                  {MONTHS.map((m, i) => {
                    const checked =
                      Array.isArray(selectedMonths) &&
                      selectedMonths.includes(i);
                    return (
                      <label
                        key={m}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedMonths((prev) => {
                              const base = Array.isArray(prev) ? prev : [];
                              return base.includes(i)
                                ? base.filter((x) => x !== i)
                                : [...base, i];
                            })
                          }
                        />
                        <span style={{ fontSize: 13 }}>{m}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "scroll",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          msOverflowStyle: "auto",
          paddingBottom: 40,
          flexWrap: "nowrap",
          scrollSnapType: "x mandatory",
          width: "100%",
          maxWidth: CARD_WIDTH,
          margin: "0 auto",
        }}
      >
        {visible.length === 0 ? (
          <div
            style={{
              color: COLORS.muted,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "48px 0",
            }}
          >
            No posts for this selection.
          </div>
        ) : (
          visible.map((p) => (
            <article
              key={p.id}
              style={{
                display: "inline-block",
                verticalAlign: "top",
                flex: `0 0 ${CARD_WIDTH}px`,
                minWidth: CARD_WIDTH,
                marginRight: 12,
                background: "#fff",
                padding: 12,
                borderRadius: 8,
                boxShadow: SHADOW.sm,
                border: `1px solid ${COLORS.border}`,
                textAlign: "center",
                scrollSnapAlign: "center",
              }}
            >
              <div style={{ fontSize: 14, marginBottom: 6, width: "100%" }}>
                {p.text}
              </div>
              <div style={{ fontSize: 12, color: COLORS.muted, width: "100%" }}>
                {p.createdAt ? new Date(p.createdAt).toLocaleString() : ""}
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {UPLIFT_EMOJIS.map(({ emoji }) => (
                  <div
                    key={emoji}
                    style={{
                      color: "#999999",
                      fontSize: 13,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{emoji}</span>
                    <span>{countUpliftsFor(p.id, emoji)}</span>
                  </div>
                ))}
              </div>
            </article>
          ))
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 8,
          marginTop: 40,
        }}
      >
        <button
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            background: COLORS.primary,
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
          onClick={handleExport}
        >
          Export
        </button>
        <button
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            background: COLORS.border,
            color: "#333",
            border: "none",
            cursor: "pointer",
          }}
          onClick={handleShare}
        >
          Share
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    width: 350,
    minWidth: 300,
    backgroundColor: "white",
    padding: 24,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    color: "#333",
    textAlign: "center",
  },
  seasonPrompt: {
    display: "grid",
    gap: 6,
    textAlign: "left",
    border: "1px solid #0067B3",
    borderRadius: 8,
    background: "#EDF7FC",
    color: "#174E73",
    padding: 12,
    marginBottom: 16,
    fontSize: 13,
    lineHeight: 1.4,
  },
  momentumWarning: {
    display: "grid",
    gap: 8,
    textAlign: "left",
    border: "1px solid #D4B65F",
    borderRadius: 8,
    background: "#FFF9E8",
    color: "#5A4A12",
    padding: 12,
    marginBottom: 16,
    fontSize: 13,
    lineHeight: 1.4,
  },
  momentumWarningButton: {
    justifySelf: "start",
    border: "none",
    borderRadius: 6,
    background: "#2F5D50",
    color: "#fff",
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },
  seasonSharePanel: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    background: "#F7FBF8",
    padding: 12,
    marginBottom: 16,
    textAlign: "left",
  },
  seasonShareTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
    color: COLORS.text,
  },
  seasonShareControls: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 10,
  },
  seasonShareSelect: {
    flex: 1,
    minWidth: 0,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "7px 8px",
    background: "#fff",
  },
  seasonShareButton: {
    border: "none",
    borderRadius: 6,
    padding: "8px 10px",
    background: "#2F5D50",
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },
  seasonShareCommunityList: {
    display: "grid",
    gap: 6,
    maxHeight: 132,
    overflowY: "auto",
  },
  seasonShareCommunityItem: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    fontSize: 12,
    color: COLORS.text,
  },
  seasonCard: {
    background: COLORS.card,
    borderRadius: RADIUS_LG,
    padding: SPACING.lg,
    marginBottom: SPACING.xl,
    boxShadow: SHADOW.sm,
    textAlign: "center",
  },

  seasonCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  currentSeasonBadge: {
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 999,
    padding: "4px 8px",
    background: "#EEF6F2",
    color: "#2F5D50",
  },

  seasonDescription: {
    marginTop: 10,
    marginBottom: 14,
    color: COLORS.muted,
    fontSize: 13,
    lineHeight: 1.45,
  },

  seasonSelectorWrap: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: 10,
  },

  seasonSelectorBtn: {
    borderRadius: 999,
    border: "1px solid",
    padding: "7px 11px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },

  seasonHint: {
    fontSize: 12,
    color: COLORS.muted,
    marginBottom: 14,
  },

  legendCard: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    background: "#FBFDFB",
    textAlign: "left",
  },

  legendToggleBtn: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: 0,
    marginBottom: 6,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },

  legendTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.text,
  },

  legendToggleChevron: {
    fontSize: 12,
    color: COLORS.muted,
    lineHeight: 1,
  },

  legendSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: COLORS.muted,
    marginBottom: 6,
  },

  legendGrid: {
    display: "grid",
    gap: 6,
  },

  legendItem: {
    display: "grid",
    gridTemplateColumns: "20px 1fr",
    gap: 8,
    alignItems: "start",
  },

  legendIcon: {
    fontSize: 15,
    lineHeight: "18px",
  },

  legendItemLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.text,
    lineHeight: 1.2,
  },

  legendItemMeaning: {
    fontSize: 11,
    color: COLORS.muted,
    lineHeight: 1.35,
  },

  legendFootnote: {
    marginTop: 10,
    fontSize: 11,
    color: COLORS.muted,
    lineHeight: 1.35,
  },

  solutionTitle: {
    marginTop: 14,
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },

  solutionWrap: {
    marginTop: 8,
    display: "flex",
    justifyContent: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  solutionChip: {
    fontSize: 12,
    borderRadius: 999,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    padding: "6px 10px",
  },

  chooseSeasonBtn: {
    marginTop: 14,
    border: "none",
    borderRadius: 8,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },

  treeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: SPACING.lg,
    marginTop: SPACING.md,
  },

  monthCell: {
    textAlign: "center",
  },

  treeWrapper: {
    position: "relative",
  },

  tree: {
    height: 180,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  transformationWrapper: {
    minWidth: 300,
    margin: "20px auto",
    backgroundColor: COLORS.card,
    padding: 24,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW.sm,
    textAlign: "center",
  },

  canopy: {
    position: "relative",
    width: 140,
    height: 110,
    background: "#3B7D23",
    borderRadius: "70px",
    transition: "transform 240ms ease, box-shadow 240ms ease",
    overflow: "visible",
  },

  trunk: {
    width: 22,
    height: 55,
    background: "#8B5A2B",
    borderRadius: RADIUS,
    marginTop: -6,
  },

  overlay: {
    marginTop: SPACING.sm,
    padding: SPACING.sm,
    background: COLORS.highlight,
    borderRadius: RADIUS,
    fontSize: "0.85rem",
  },

  monthLabel: {
    marginTop: SPACING.sm,
    fontWeight: 500,
  },

  milestoneA: {
    position: "absolute",
    top: -12,
    left: "12%",
    fontSize: 16,
  },

  milestoneB: {
    position: "absolute",
    top: -16,
    right: "14%",
    fontSize: 17,
  },

  milestoneC: {
    position: "absolute",
    bottom: -6,
    left: "44%",
    transform: "translateX(-50%)",
    fontSize: 16,
  },

  milestoneD: {
    position: "absolute",
    bottom: -8,
    right: "20%",
    fontSize: 16,
  },

  milestoneE: {
    position: "absolute",
    top: "12%",
    left: "52%",
    fontSize: 16,
  },

  milestoneF: {
    position: "absolute",
    top: "42%",
    left: "8%",
    fontSize: 14,
  },

  milestoneG: {
    position: "absolute",
    top: -8,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 18,
  },
};
